/*
 * Shaker-offer landing controller.
 *
 * The landing page is split across several theme sections (promo bar, hero,
 * bundle, education, pain, offer, FAQ, sticky bar). This single controller is
 * loaded once per page via the `shaker-assets` snippet and wires up behaviour
 * across all of them using document-level selectors rather than a single
 * section root. It is idempotent: if the snippet ends up emitting the script
 * more than once, only the first execution does anything.
 */
(function () {
  if (window.__shakerOfferInit) return;
  window.__shakerOfferInit = true;

  function init() {
    /* ---------- Portal promo bar into the theme's sticky header ---------- */
    var promo = document.querySelector('.so__promo');
    var headerSection = document.querySelector('.header-section');
    if (promo && headerSection) {
      headerSection.prepend(promo);
      var syncPromoHeight = function () {
        var h = promo.offsetHeight;
        document.documentElement.style.setProperty('--promo-h', h + 'px');
      };
      syncPromoHeight();
      window.addEventListener('resize', syncPromoHeight);
      if (window.ResizeObserver) {
        new ResizeObserver(syncPromoHeight).observe(promo);
      }

      // Hysteresis: hide above HIDE_AT, only re-show once you scroll back
      // below SHOW_AT. The gap stops the layout-shift-induced scrollY blip
      // (the page jumps up by the promo's height when the menu rises) from
      // immediately flipping state back on.
      var HIDE_AT = 600;
      var SHOW_AT = 400;
      var hidden = false;
      var onPromoScroll = function () {
        var y = window.scrollY;
        var shouldHide = hidden ? y > SHOW_AT : y > HIDE_AT;
        if (shouldHide === hidden) return;
        hidden = shouldHide;
        document.body.classList.toggle('so-promo-hidden', hidden);
      };
      window.addEventListener('scroll', onPromoScroll, { passive: true });
      onPromoScroll();
    }

    /* ---------- Claim buttons → trigger the real add-to-cart ---------- */
    // Queried document-wide (after the promo has been portaled into the
    // header) so claim triggers in any section are picked up.
    var claimButtons = Array.prototype.slice.call(document.querySelectorAll('.js-claim'));

    // Wrap each claim button's label in a text span + inject a spinner so we
    // can swap between them while the add-to-cart request is in flight.
    claimButtons.forEach(function (link) {
      if (link.querySelector('.js-claim__text')) return;
      var textSpan = document.createElement('span');
      textSpan.className = 'js-claim__text';
      while (link.firstChild) {
        textSpan.appendChild(link.firstChild);
      }
      link.appendChild(textSpan);

      var spinner = document.createElement('span');
      spinner.className = 'js-claim__spinner';
      spinner.setAttribute('aria-hidden', 'true');
      spinner.innerHTML =
        '<svg class="spinner" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">' +
        '<circle class="spinner__circle" cx="12" cy="12" r="10" fill="none" stroke-width="2"></circle>' +
        '</svg>';
      link.appendChild(spinner);
    });

    function clearClaimLoading() {
      claimButtons.forEach(function (link) {
        link.classList.remove('is-claiming');
      });
    }

    // Revert all claim buttons once the cart has finished updating. cart:add
    // fires after the item is added; the drawer then refreshes + opens, so we
    // wait for the drawer to be visible (with a timeout fallback) before
    // restoring the button text.
    document.addEventListener('cart:add', function () {
      var drawer = document.querySelector('[data-cart-drawer]');
      if (!drawer) {
        clearClaimLoading();
        return;
      }
      if (drawer.getAttribute('aria-hidden') === 'false') {
        clearClaimLoading();
        return;
      }
      var done = false;
      var finish = function () {
        if (done) return;
        done = true;
        observer.disconnect();
        clearTimeout(fallback);
        clearClaimLoading();
      };
      var observer = new MutationObserver(function () {
        if (drawer.getAttribute('aria-hidden') === 'false') finish();
      });
      observer.observe(drawer, { attributes: true, attributeFilter: ['aria-hidden'] });
      var fallback = setTimeout(finish, 4000);
    });

    claimButtons.forEach(function (link) {
      link.addEventListener('click', function (e) {
        var atc = document.querySelector('[data-add-to-cart-button]');
        if (!atc) return;
        e.preventDefault();
        link.classList.add('is-claiming');
        // Safety net: if the add fails (no cart:add fires), don't spin forever.
        setTimeout(clearClaimLoading, 8000);
        atc.click();
      });
      // Non-anchor triggers (e.g. the promo bar div[role=button]) need
      // explicit keyboard activation to stay accessible.
      if (link.getAttribute('role') === 'button') {
        link.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            link.click();
          }
        });
      }
    });

    /* ---------- Smooth-scroll helper ---------- */
    function smoothScrollTo(target) {
      var stickyOffset = (document.querySelector('.header-section') || {}).offsetHeight || 0;
      var startY = window.scrollY;
      var endY = target.getBoundingClientRect().top + startY - stickyOffset;
      var duration = 400;
      var startT = performance.now();
      var easeInOut = function (t) {
        return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
      };
      var step = function (now) {
        var t = Math.min(1, (now - startT) / duration);
        window.scrollTo(0, startY + (endY - startY) * easeInOut(t));
        if (t < 1) requestAnimationFrame(step);
      };
      requestAnimationFrame(step);
    }

    /* ---------- Smooth-scroll for in-page anchor links ---------- */
    // Scoped to landing-page anchors (inside a `.so` section wrapper) so we
    // don't hijack the theme header's own in-page links. `.js-anchor` bars are
    // excluded here and handled below so they aren't bound twice.
    document.querySelectorAll('.so a[href^="#"]:not(.js-anchor)').forEach(function (link) {
      link.addEventListener('click', function (e) {
        var id = link.getAttribute('href').slice(1);
        if (!id) return;
        var target = document.getElementById(id);
        if (!target) return;
        e.preventDefault();
        smoothScrollTo(target);
      });
    });

    /* ---------- Bars set to "scroll to offer" instead of add-to-cart ---------- */
    // Queried document-wide so the promo bar (now portaled into the header) is
    // picked up alongside the footer sticky bar. Works for both <a> and
    // div[role=button] triggers.
    document.querySelectorAll('.js-anchor').forEach(function (el) {
      var targetId = el.getAttribute('data-anchor-target');
      el.addEventListener('click', function (e) {
        var target = targetId ? document.getElementById(targetId) : null;
        if (!target) return;
        e.preventDefault();
        smoothScrollTo(target);
      });
      if (el.getAttribute('role') === 'button') {
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            el.click();
          }
        });
      }
    });

    /* ---------- Variant selector ---------- */
    var variantButtons = document.querySelectorAll('.js-variants .so-variant');
    variantButtons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        variantButtons.forEach(function (b) {
          b.classList.remove('is-selected');
        });
        btn.classList.add('is-selected');
      });
    });

    /* ---------- FAQ accordions ---------- */
    document.querySelectorAll('.js-faq .so-faq__item').forEach(function (item) {
      item.addEventListener('click', function () {
        item.classList.toggle('is-open');
      });
    });

    /* ---------- Countdown ---------- */
    var sticky = document.querySelector('.js-sticky');
    var endsAt = sticky ? sticky.getAttribute('data-offer-ends') : null;
    var endsMs = endsAt ? new Date(endsAt).getTime() : 0;
    var cD = document.querySelector('.js-c-d');
    var cH = document.querySelector('.js-c-h');
    var cM = document.querySelector('.js-c-m');
    var cS = document.querySelector('.js-c-s');
    function pad(n) {
      return n < 10 ? '0' + n : '' + n;
    }
    function tick() {
      if (!endsMs) return;
      var diff = Math.max(0, endsMs - Date.now());
      var d = Math.floor(diff / 86400000);
      var h = Math.floor((diff % 86400000) / 3600000);
      var m = Math.floor((diff % 3600000) / 60000);
      var s = Math.floor((diff % 60000) / 1000);
      if (cD) cD.textContent = pad(d);
      if (cH) cH.textContent = pad(h);
      if (cM) cM.textContent = pad(m);
      if (cS) cS.textContent = pad(s);
    }
    tick();
    setInterval(tick, 1000);

    /* ---------- Sticky bar reveal on scroll ---------- */
    // The footer bar should appear exactly as the header promo bar hides, then
    // stay pinned to the bottom all the way down the page. It therefore uses the
    // SAME hysteresis thresholds as the promo hide logic above (HIDE_AT 600 /
    // SHOW_AT 400): show once you scroll past 600 (where the promo collapses),
    // and only hide again once you scroll back above 400 (where it returns).
    if (sticky) {
      var FOOTER_SHOW_AT = 600; // matches promo HIDE_AT
      var FOOTER_HIDE_AT = 400; // matches promo SHOW_AT
      var footerShown = false;
      var onStickyScroll = function () {
        var y = window.scrollY;
        var shouldShow = footerShown ? y > FOOTER_HIDE_AT : y > FOOTER_SHOW_AT;
        if (shouldShow === footerShown) return;
        footerShown = shouldShow;
        sticky.classList.toggle('is-visible', shouldShow);
      };
      window.addEventListener('scroll', onStickyScroll, { passive: true });
      onStickyScroll();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
